import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { MatrixItem, MatrixKleur } from '../../services/agendaService';
import { KLEUR_KLASSE, KLEUR_STAAL, LEGENDA, blokStijl } from './kleuren';

/**
 * Outlook-stijl matrix van het Agenda Matrix Center: dezelfde werkbalk,
 * tijdas en grid als de opdrachtgevers-agenda (B2bAgenda), met 30-minuten
 * segmenten van 07:00 tot 22:00 en click-and-drag om een periode te trekken.
 * Puur presentatie: data en acties komen van de pagina.
 */

export type MatrixWeergave = 'dag' | 'week' | 'maand';

export const START_UUR = 7;
export const EIND_UUR = 22;
const UUR_HOOGTE = 48; // px
const SEGMENT_MINUTEN = 30;
const SEGMENT_HOOGTE = UUR_HOOGTE / 2;
const SEGMENTEN = ((EIND_UUR - START_UUR) * 60) / SEGMENT_MINUTEN;
const UREN = Array.from({ length: EIND_UUR - START_UUR }, (_, i) => START_UUR + i);

/** Achtergrondlaag over de volle kolombreedte: open tijd en afwezigheid. */
const ACHTERGROND: MatrixKleur[] = ['mint', 'zand'];

/** Zichtbare dagen voor een weergave rond `anker`. */
export function dagenVoor(weergave: MatrixWeergave, anker: Date): Date[] {
  if (weergave === 'dag') return [startOfDay(anker)];
  if (weergave === 'week') {
    const ma = startOfWeek(anker, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(ma, i));
  }
  const lijst: Date[] = [];
  const tot = endOfWeek(endOfMonth(anker), { weekStartsOn: 1 });
  for (let dag = startOfWeek(startOfMonth(anker), { weekStartsOn: 1 }); dag <= tot; dag = addDays(dag, 1)) lijst.push(dag);
  return lijst;
}

interface Geplaatst {
  item: MatrixItem;
  top: number;
  hoogte: number;
  kolom: number;
  kolommen: number;
}

/** Deel van een item binnen de zichtbare uren van `dag`, in uren vanaf middernacht. */
function bereikOpDag(item: MatrixItem, dag: Date): { s: number; e: number } | null {
  const dagStart = startOfDay(dag).getTime();
  const s = Math.max((new Date(item.start).getTime() - dagStart) / 3600000, START_UUR);
  const e = Math.min((new Date(item.eind).getTime() - dagStart) / 3600000, EIND_UUR);
  return e > s ? { s, e } : null;
}

/** Overlappende items naast elkaar, zoals Outlook. */
function plaatsOpDag(items: MatrixItem[], dag: Date): Geplaatst[] {
  const lijst = items
    .map((item) => ({ item, bereik: bereikOpDag(item, dag) }))
    .filter((x): x is { item: MatrixItem; bereik: { s: number; e: number } } => x.bereik !== null)
    .map(({ item, bereik }) => ({ item, s: bereik.s, e: Math.max(bereik.e, bereik.s + 0.25) }))
    .sort((x, y) => x.s - y.s || y.e - x.e);

  const resultaat: Geplaatst[] = [];
  let cluster: Array<{ item: MatrixItem; s: number; e: number; kolom: number }> = [];
  let clusterEind = -1;
  const sluitCluster = () => {
    const kolommen = Math.max(1, ...cluster.map((c) => c.kolom + 1));
    for (const c of cluster) {
      resultaat.push({ item: c.item, top: (c.s - START_UUR) * UUR_HOOGTE, hoogte: (c.e - c.s) * UUR_HOOGTE, kolom: c.kolom, kolommen });
    }
    cluster = [];
  };
  for (const it of lijst) {
    if (cluster.length && it.s >= clusterEind) sluitCluster();
    const bezet = new Set(cluster.filter((c) => c.e > it.s).map((c) => c.kolom));
    let kolom = 0;
    while (bezet.has(kolom)) kolom++;
    cluster.push({ ...it, kolom });
    clusterEind = Math.max(clusterEind, it.e);
  }
  if (cluster.length) sluitCluster();
  return resultaat;
}

const tijd = (d: Date | string) => format(new Date(d), 'HH:mm');

/** Segmentindex → tijdstip op `dag`. */
function segmentTijd(dag: Date, index: number): Date {
  const d = new Date(startOfDay(dag));
  d.setMinutes(START_UUR * 60 + index * SEGMENT_MINUTEN);
  return d;
}

export interface MatrixConcept {
  start: Date;
  eind: Date;
  kleur: MatrixKleur | null;
}

interface AgendaMatrixProps {
  items: MatrixItem[];
  weergave: MatrixWeergave;
  anker: Date;
  onWeergave: (w: MatrixWeergave) => void;
  onAnker: (d: Date) => void;
  onItemClick: (item: MatrixItem) => void;
  /** Periode getrokken (muis losgelaten) */
  onSelectie: (start: Date, eind: Date) => void;
  /** Getrokken periode die nog in de pop-up staat; kleurt mee met de gekozen optie */
  concept?: MatrixConcept | null;
  geselecteerdId?: string | null;
  laden?: boolean;
}

export const AgendaMatrix: React.FC<AgendaMatrixProps> = ({
  items,
  weergave,
  anker,
  onWeergave,
  onAnker,
  onItemClick,
  onSelectie,
  concept,
  geselecteerdId,
  laden,
}) => {
  const { t } = useTranslation('agenda');
  const vandaag = new Date();
  const dagen = useMemo(() => dagenVoor(weergave, anker), [weergave, anker]);
  const [sleep, setSleep] = useState<{ dag: Date; van: number; tot: number } | null>(null);
  const sleepRef = useRef(sleep);
  sleepRef.current = sleep;

  const weergaven: Array<{ key: MatrixWeergave; label: string }> = [
    { key: 'dag', label: t('matrix.weergave.dag', 'Dag') },
    { key: 'week', label: t('matrix.weergave.week', 'Week') },
    { key: 'maand', label: t('matrix.weergave.maand', 'Maand') },
  ];

  const blader = (richting: 1 | -1) => {
    if (weergave === 'maand') onAnker(addMonths(anker, richting));
    else if (weergave === 'dag') onAnker(addDays(anker, richting));
    else onAnker(addWeeks(anker, richting));
  };

  const titel =
    weergave === 'maand'
      ? format(anker, 'MMMM yyyy', { locale: nl })
      : weergave === 'dag'
      ? format(anker, 'EEEE d MMMM yyyy', { locale: nl })
      : `${format(dagen[0], 'd MMM', { locale: nl })} – ${format(dagen[dagen.length - 1], 'd MMM yyyy', { locale: nl })}`;

  const opDag = (dag: Date) => items.filter((i) => !i.hele_dag && bereikOpDag(i, dag) !== null);
  const heleDag = (dag: Date) => items.filter((i) => i.hele_dag && isSameDay(new Date(i.start), dag));

  // ── Click-and-drag ──────────────────────────────────────────────────────
  const segmentVan = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return Math.min(SEGMENTEN - 1, Math.max(0, Math.floor((e.clientY - rect.top) / SEGMENT_HOOGTE)));
  };
  const begin = (dag: Date, e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const index = segmentVan(e);
    setSleep({ dag, van: index, tot: index });
  };
  const beweeg = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!sleepRef.current) return;
    const index = segmentVan(e);
    if (index !== sleepRef.current.tot) setSleep({ ...sleepRef.current, tot: index });
  };
  const loslaten = () => {
    const s = sleepRef.current;
    if (!s) return;
    setSleep(null);
    const van = Math.min(s.van, s.tot);
    const tot = Math.max(s.van, s.tot) + 1;
    // Eén klik (niet gesleept) op open tijd of afwezigheid opent dat blok
    if (s.van === s.tot) {
      const moment = segmentTijd(s.dag, van).getTime() + 1;
      const geraakt = items.find(
        (i) => ACHTERGROND.includes(i.kleur) && !i.hele_dag && new Date(i.start).getTime() < moment && new Date(i.eind).getTime() > moment
      );
      if (geraakt) {
        onItemClick(geraakt);
        return;
      }
    }
    onSelectie(segmentTijd(s.dag, van), segmentTijd(s.dag, tot));
  };

  const conceptOpDag = (dag: Date) => {
    if (sleep && isSameDay(sleep.dag, dag)) {
      const van = Math.min(sleep.van, sleep.tot);
      const tot = Math.max(sleep.van, sleep.tot) + 1;
      return { top: van * SEGMENT_HOOGTE, hoogte: (tot - van) * SEGMENT_HOOGTE, label: `${tijd(segmentTijd(dag, van))}–${tijd(segmentTijd(dag, tot))}`, kleur: null as MatrixKleur | null };
    }
    if (concept && isSameDay(concept.start, dag)) {
      const dagStart = startOfDay(dag).getTime();
      const s = Math.max((concept.start.getTime() - dagStart) / 3600000, START_UUR);
      const e = Math.min((concept.eind.getTime() - dagStart) / 3600000, EIND_UUR);
      if (e <= s) return null;
      return { top: (s - START_UUR) * UUR_HOOGTE, hoogte: (e - s) * UUR_HOOGTE, label: `${tijd(concept.start)}–${tijd(concept.eind)}`, kleur: concept.kleur };
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 flex flex-col">
      {/* Werkbalk */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-gray-200">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onAnker(new Date())} className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50">
            {t('matrix.vandaag', 'Vandaag')}
          </button>
          <button type="button" onClick={() => blader(-1)} className="p-1.5 rounded-md hover:bg-gray-100" aria-label={t('matrix.vorige', 'Vorige')}>
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button type="button" onClick={() => blader(1)} className="p-1.5 rounded-md hover:bg-gray-100" aria-label={t('matrix.volgende', 'Volgende')}>
            <ChevronRight className="w-5 h-5" />
          </button>
          <h2 className="ml-2 text-base font-semibold text-gray-900 capitalize">{titel}</h2>
          {laden && <span className="ml-2 text-xs text-gray-400">{t('matrix.laden', 'Laden…')}</span>}
        </div>
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
          {weergaven.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => onWeergave(w.key)}
              className={`px-3 py-1.5 text-sm font-medium ${weergave === w.key ? 'bg-navy-900 text-white shadow-[inset_0_-3px_0_#E5A425]' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {weergave === 'maand' ? (
        <div>
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
            {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((dag) => (
              <div key={dag} className="px-2 py-1.5">{dag}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {dagen.map((dag) => {
              const lijst = [...heleDag(dag), ...opDag(dag)].filter((i) => i.kleur !== 'mint' && i.kleur !== 'reistijd');
              const openUren = opDag(dag)
                .filter((i) => i.kleur === 'mint')
                .reduce((som, i) => {
                  const b = bereikOpDag(i, dag);
                  return som + (b ? b.e - b.s : 0);
                }, 0);
              return (
                <div
                  key={dag.toISOString()}
                  onClick={() => {
                    onAnker(dag);
                    onWeergave('dag');
                  }}
                  className={`min-h-[104px] border-b border-r border-gray-100 p-1 cursor-pointer hover:bg-gray-50 ${isSameMonth(dag, anker) ? '' : 'bg-gray-50/60 text-gray-400'}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className={`text-xs w-6 h-6 flex items-center justify-center rounded-full ${isSameDay(dag, vandaag) ? 'bg-navy-800 text-white font-semibold' : ''}`}>
                      {format(dag, 'd')}
                    </div>
                    {openUren > 0 && (
                      <span className="text-[10px] px-1 rounded bg-emerald-50 border border-emerald-300 text-emerald-800">
                        {t('matrix.openUren', { defaultValue: '{{uren}} u open', uren: Math.round(openUren * 10) / 10 })}
                      </span>
                    )}
                  </div>
                  {lijst.slice(0, 3).map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemClick(i);
                      }}
                      style={blokStijl(i)}
                      className={`block w-full text-left truncate text-[11px] leading-4 px-1 mb-0.5 rounded border-l-4 ${KLEUR_KLASSE[i.kleur]}`}
                    >
                      {!i.hele_dag && `${tijd(i.start)} `}
                      {i.titel}
                    </button>
                  ))}
                  {lijst.length > 3 && (
                    <div className="text-[11px] text-gray-500 px-1">{t('matrix.meer', { defaultValue: '+{{count}} meer', count: lijst.length - 3 })}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="overflow-auto max-h-[720px] select-none">
          <div className={weergave === 'week' ? 'min-w-[560px]' : ''}>
            {/* Dagkoppen + hele-dag-strook */}
            <div className="flex sticky top-0 z-20 bg-white border-b border-gray-200">
              <div className="w-14 flex-shrink-0" />
              {dagen.map((dag) => (
                <div key={dag.toISOString()} className="flex-1 min-w-0 px-2 py-1.5 border-l border-gray-100">
                  <div className="text-xs uppercase text-gray-500">{format(dag, 'EEE', { locale: nl })}</div>
                  <div className={`text-lg font-semibold ${isSameDay(dag, vandaag) ? 'text-navy-800' : 'text-gray-900'}`}>{format(dag, 'd')}</div>
                  {heleDag(dag).map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => onItemClick(i)}
                      className={`mt-0.5 block w-full text-left truncate text-[11px] leading-4 px-1 rounded border-l-4 ${KLEUR_KLASSE[i.kleur]} ${geselecteerdId === i.id ? 'ring-2 ring-navy-800' : ''}`}
                    >
                      {i.titel}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            {/* Tijdraster */}
            <div className="flex">
              <div className="w-14 flex-shrink-0 relative" style={{ height: UREN.length * UUR_HOOGTE }}>
                {UREN.map((u, i) => (
                  <div key={u} style={{ top: Math.max(i * UUR_HOOGTE - 7, 0) }} className="absolute right-0 text-[11px] leading-[14px] text-gray-400 pr-2">
                    {String(u).padStart(2, '0')}:00
                  </div>
                ))}
              </div>
              {dagen.map((dag) => {
                const dagItems = opDag(dag);
                const achtergrond = dagItems.filter((i) => ACHTERGROND.includes(i.kleur));
                const voorgrond = plaatsOpDag(dagItems.filter((i) => !ACHTERGROND.includes(i.kleur)), dag);
                const c = conceptOpDag(dag);
                return (
                  <div
                    key={dag.toISOString()}
                    onPointerDown={(e) => begin(dag, e)}
                    onPointerMove={beweeg}
                    onPointerUp={loslaten}
                    onPointerCancel={() => setSleep(null)}
                    className={`flex-1 min-w-0 relative border-l border-gray-100 cursor-cell touch-none ${isSameDay(dag, vandaag) ? 'bg-navy-50/40' : ''}`}
                    style={{ height: UREN.length * UUR_HOOGTE }}
                  >
                    {UREN.map((u) => (
                      <div key={u} style={{ height: UUR_HOOGTE }} className="border-b border-gray-100 pointer-events-none">
                        <div className="border-b border-dashed border-gray-50" style={{ height: SEGMENT_HOOGTE }} />
                      </div>
                    ))}
                    {achtergrond.map((i) => {
                      const b = bereikOpDag(i, dag)!;
                      return (
                        <div
                          key={i.id}
                          title={i.titel}
                          className={`absolute inset-x-0 pointer-events-none border-l-4 px-1.5 text-[11px] overflow-hidden ${KLEUR_KLASSE[i.kleur]} ${i.kleur === 'mint' ? 'opacity-90' : 'opacity-80'} ${geselecteerdId === i.id ? 'ring-2 ring-inset ring-navy-800' : ''}`}
                          style={{ top: (b.s - START_UUR) * UUR_HOOGTE, height: (b.e - b.s) * UUR_HOOGTE, ...blokStijl(i) }}
                        >
                          <span className="font-medium">{i.titel}</span>
                        </div>
                      );
                    })}
                    {isSameDay(dag, vandaag) && vandaag.getHours() >= START_UUR && vandaag.getHours() < EIND_UUR && (
                      <div
                        className="absolute left-0 right-0 border-t-2 border-red-500 z-10 pointer-events-none"
                        style={{ top: (vandaag.getHours() + vandaag.getMinutes() / 60 - START_UUR) * UUR_HOOGTE }}
                      />
                    )}
                    {voorgrond.map(({ item: i, top, hoogte, kolom, kolommen }) => (
                      <button
                        key={i.id}
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => onItemClick(i)}
                        title={`${i.titel}${i.ondertitel ? ` — ${i.ondertitel}` : ''}`}
                        className={`absolute z-[5] text-left rounded border-l-4 px-1.5 py-0.5 overflow-hidden text-xs shadow-sm hover:shadow ${KLEUR_KLASSE[i.kleur]} ${geselecteerdId === i.id ? 'ring-2 ring-navy-800' : ''}`}
                        style={{
                          top: top + 1,
                          height: Math.max(hoogte - 2, 12),
                          left: `calc(${(kolom / kolommen) * 100}% + 2px)`,
                          width: `calc(${100 / kolommen}% - 4px)`,
                          ...blokStijl(i),
                        }}
                      >
                        {i.kleur === 'reistijd' ? (
                          <div className="truncate text-[10px]">{t('matrix.reistijdKort', { defaultValue: 'Reistijd {{min}} min', min: i.meta.minuten })}</div>
                        ) : (
                          <>
                            <div className="font-semibold truncate">{i.titel}</div>
                            <div className="truncate opacity-80">
                              {tijd(i.start)}–{tijd(i.eind)}
                              {i.ondertitel ? ` · ${i.ondertitel}` : ''}
                            </div>
                          </>
                        )}
                      </button>
                    ))}
                    {c && (
                      <div
                        className={`absolute inset-x-1 z-[6] rounded border-2 px-1.5 py-0.5 text-xs font-semibold pointer-events-none ${c.kleur ? KLEUR_KLASSE[c.kleur] : 'bg-navy-100/70 border-navy-500 border-dashed text-navy-900'}`}
                        style={{ top: c.top, height: c.hoogte }}
                      >
                        {c.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Legenda */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 px-3 py-2 border-t border-gray-200 text-xs text-gray-600">
        {LEGENDA.map((r) => (
          <span key={r.kleur} className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded-sm border ${KLEUR_STAAL[r.kleur]}`} style={r.kleur === 'reistijd' ? blokStijl({ kleur: 'reistijd', wacht_op_bevestiging: false }) : undefined} />
            {t(r.labelKey, r.label)}
          </span>
        ))}
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm border border-dashed border-gray-500" style={blokStijl({ kleur: 'blauw', wacht_op_bevestiging: true })} />
          {t('matrix.kleur.gestreept', 'Gestreept = wacht op bevestiging')}
        </span>
      </div>
    </div>
  );
};
